/**
 * A list slot's default name by its position — "Main List" for the first,
 * "List n" (1-based) for every one after it. Shared by the create form's
 * newItemDefaults (a brand-new slot) and the edit page (an existing slot
 * whose label was never set), so both land on the same naming convention.
 */
export function defaultListSlotLabel(index: number): string {
    return index === 0 ? 'Main List' : `List ${index + 1}`;
}

/**
 * Fills in a default label for any slot that doesn't have a real one, by
 * position, without ever overwriting a label the organiser actually typed.
 * A single unnamed slot is left alone — there's nothing else it could be —
 * matching ListSlotLabelPipe's own "a lone slot doesn't need a name" rule.
 */
export function withDefaultListSlotLabels<T extends { label?: string }>(slots: T[] | undefined | null): T[] {
    if (!slots || slots.length <= 1) {
        return slots ?? [];
    }
    return slots.map((slot, index) => (slot.label?.trim() ? slot : { ...slot, label: defaultListSlotLabel(index) }));
}

/**
 * The array form control's item header (the collapsed row you tap to expand
 * a slot) for the listSlots field — a `mapItemName` callback, the form
 * system's existing hook for a field to fully control its own item labels
 * (see array-field-item-name.pipe.ts). Falls back to `defaultListSlotLabel`
 * by the slot's current position rather than the generic "(New Slot)",
 * found by matching the array item form control's auto-assigned `id` against
 * the live form array — reliable even after items are reordered or deleted,
 * since it locates the slot's current position rather than assuming one.
 */
export function listSlotItemName(
    value: { id?: number; label?: string } | undefined,
    formValue: { listSlots?: { id?: number }[] } | undefined
): string {
    const label = value?.label?.trim();
    if (label) {
        return label;
    }
    const slots = formValue?.listSlots ?? [];
    const index = slots.findIndex((s) => s?.id === value?.id);
    return defaultListSlotLabel(index >= 0 ? index : Math.max(slots.length - 1, 0));
}
